import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Index from "./pages/Index";
import MetaPixelRouteTracker from "./components/MetaPixelRouteTracker";

// Lazy-load all secondary routes so they don't bloat the initial bundle
const GetStarted = lazy(() => import("./pages/GetStarted"));
const Packages = lazy(() => import("./pages/Packages"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PatientAcquisition = lazy(() => import("./pages/PatientAcquisition"));
const LaunchYourBrand = lazy(() => import("./pages/LaunchYourBrand"));
const LaunchAcquire = lazy(() => import("./pages/LaunchAcquire"));
const AiCreative = lazy(() => import("./pages/AiCreative"));
const TikTokGlp1CaseStudy = lazy(() => import("./pages/TikTokGlp1CaseStudy"));
const Glp1CaseStudy = lazy(() => import("./pages/Glp1CaseStudy"));

const legacyPackagePaths = ["/starter", "/growth", "/special", "/premium", "/premium-plus", "/scale", "/performance", "/budget-performance", "/budget"];

const queryClient = new QueryClient();

function RouteScroll() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return; }
    let id: string;
    try { id = decodeURIComponent(hash.slice(1)); } catch { return; }
    const timer = window.setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "instant" }), 160);
    return () => window.clearTimeout(timer);
  }, [pathname, hash]);
  return null;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <RouteScroll />
        <MetaPixelRouteTracker skipPaths={legacyPackagePaths} />
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/get-started" element={<GetStarted />} />
            <Route path="/packages" element={<Packages />} />
            <Route path="/patient-acquisition" element={<PatientAcquisition />} />
             <Route path="/launch-your-brand" element={<LaunchYourBrand />} />
            <Route path="/launch" element={<LaunchAcquire />} />
            <Route path="/ai-creative" element={<AiCreative />} />
            <Route path="/case-studies/glp-1-acquisition" element={<Glp1CaseStudy />} />
            <Route path="/case-studies/tiktok-glp-1-growth" element={<TikTokGlp1CaseStudy />} />
            {legacyPackagePaths.map((path) => <Route key={path} path={path} element={<Navigate to="/packages" replace />} />)}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
